import { useState } from 'react';

import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';

import DialogHeader from 'components/DialogHeader';
import SearchBar from 'components/SearchBar';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchRespostasProntas,
  mensagensSelector,
  resetRespostasProntas,
  RespostaProntaInterface,
} from 'store/reducers/mensagens';

type ModalRespostasProntasProps = {
  open: boolean;
  onClose: () => void;
  onSelect?: (texto: string) => void;
};

const useStyles = makeStyles(({ palette, spacing }) => ({
  dialogPaper: {
    height: '80%',
    overflow: 'hidden',
  },
  list: {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: spacing(1),
  },
  preview: {
    height: '100%',
    userSelect: 'none',
    overflowX: 'hidden',
    overflowY: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      width: 0,
    },
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
    padding: spacing(2),
    backgroundColor: palette.background.default,
  },
}));

export default function ModalRespostasProntas(
  props: ModalRespostasProntasProps,
) {
  const { open, onClose, onSelect } = props;
  const classes = useStyles();
  const [preview, setPreview] = useState('');
  const dispatch = useAppDispatch();
  const { respostasProntas, loadingRespostasProntas } =
    useAppSelector(mensagensSelector);
  const hasRespostas = !loadingRespostasProntas && respostasProntas.length > 0;

  const onReloadRespostasProntas = (filter: string) => {
    dispatch(resetRespostasProntas());
    dispatch(fetchRespostasProntas(filter));
  };

  const handleSelect = (texto: string) => {
    onClose();
    if (onSelect) onSelect(texto);
  };

  const items = loadingRespostasProntas
    ? Array.from(new Array(6) as RespostaProntaInterface[])
    : respostasProntas;
  const renderRespostas = items.map((respostaPronta, index) => {
    const key = respostaPronta ? respostaPronta.id : index;

    if (respostaPronta) {
      const { nome, resposta } = respostaPronta;

      return (
        <ListItem
          key={key}
          onMouseEnter={() => setPreview(resposta.texto)}
          onClick={() => handleSelect(resposta.texto)}
          button
        >
          <ListItemText
            primary={nome}
            primaryTypographyProps={{ noWrap: true }}
          />
        </ListItem>
      );
    }

    return (
      <ListItem key={key} disableGutters>
        <ListItemText primary={<Skeleton height={24} variant="rect" />} />
      </ListItem>
    );
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
      TransitionProps={{
        onEnter: () => onReloadRespostasProntas(''),
        onExited: () => setPreview(''),
      }}
    >
      <DialogHeader
        title="Respostas prontas"
        description="Clique para selecionar uma resposta"
        onClose={onClose}
      />

      <Box ml={3} mr={3} mb={1}>
        <SearchBar
          onChange={onReloadRespostasProntas}
          onCancelSearch={() => onReloadRespostasProntas('')}
        />
      </Box>

      <DialogContent style={{ overflow: 'hidden' }}>
        <Grid container spacing={2} component={Box} height="100%">
          <Grid item xs component={Box} height="100%" overflow="hidden">
            <List className={classes.list} disablePadding dense>
              {renderRespostas}

              {!hasRespostas && !loadingRespostasProntas && (
                <ListItem disableGutters>
                  <ListItemText secondary="Não encontramos nenhuma resposta pronta." />
                </ListItem>
              )}
            </List>
          </Grid>

          <Grid item xs component={Box} height="100%" overflow="hidden">
            <Paper className={classes.preview} elevation={0}>
              <Typography color="textSecondary" variant="caption">
                {hasRespostas && preview ? preview : 'Pré-visualização'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
